import { Component, EventEmitter, Input, Output } from '@angular/core'
import { ScopeElementsConfig, UpdateFeeItemPayload, UpdateRolePayload, UpdateTpcPayload } from '../models'

import { CdkDragDrop } from '@angular/cdk/drag-drop'
import { FormControl } from '@angular/forms'
import { MatDialog } from '@angular/material/dialog'
import { MenuOptions } from '@app/core/model/definitions/menu-options.interface'
import { ModalConfig } from '@app/core/model/modal-config.model'
import { AuthService } from '@app/core/service/auth.service'
import { ScopeComponent } from '@app/features/scoping/models/component.model'
import { Deliverable } from '@app/features/scoping/models/deliverable.model'
import { preventDefault } from '@app/shared/utils/prevent-default.util'
import { UpdateComponentPayload } from '../models/update-component-payload.interface'
import { trackById } from '@shared/utils/utils'
import { plainToInstance } from 'class-transformer'
import { cloneDeep } from 'lodash'
import { ScopeUiModalComponent } from '../../ui-components/scope-ui-modal/scope-ui-modal.component'
import { ScopeElementsService } from '../services/scope-elements.service'

@Component({
  selector: 'scope-components',
  templateUrl: './components.component.html',
})
export class ComponentsComponent {
  @Input() config: ScopeElementsConfig
  @Input() deliverable: Deliverable
  @Input() components: ScopeComponent[]
  @Input() level: number
  @Input() componentMenuOptions: MenuOptions[]
  @Input() feeMenuOptions: MenuOptions[]

  @Output() onUpdateComponent = new EventEmitter<UpdateComponentPayload>()
  @Output() onOverrideQuantity = new EventEmitter<ScopeComponent>()
  @Output() onOverrideRateCardRoleHours = new EventEmitter<UpdateRolePayload>()
  @Output() onMoveComponent = new EventEmitter<{ component: ScopeComponent; deliverable: Deliverable }>()
  @Output() onMoveComponentOutsideDeliverable = new EventEmitter<ScopeComponent>()
  @Output() onUpdateComponentFee = new EventEmitter<UpdateFeeItemPayload>()
  @Output() onUpdateRole = new EventEmitter<UpdateRolePayload>()
  @Output() onUpdateComponentTpc = new EventEmitter<UpdateTpcPayload>()
  @Output() onReorderComponentTpcs = new EventEmitter<any>()

  constructor(
    private dialog: MatDialog,
    protected authService: AuthService,
    protected scopeElementsService: ScopeElementsService
  ) {}

  moveComponent(deliverable: Deliverable, component: ScopeComponent) {
    this.onMoveComponent.emit({ component, deliverable })
  }

  moveComponentOutsideDeliverable(component: ScopeComponent, deliverable: Deliverable) {
    this.onMoveComponentOutsideDeliverable.emit(plainToInstance(ScopeComponent, { ...component, deliverable }))
  }

  onDragComponent(event: any) {
    preventDefault(event)
    this.scopeElementsService.deliverableDragging = false
    this.scopeElementsService.stageDragging = false
    this.scopeElementsService.componentDragging = true
  }

  dropComponent(event: CdkDragDrop<ScopeComponent[]>, deliverable: Deliverable) {
    const del = cloneDeep(deliverable)
    const currentComponent: ScopeComponent = event.item.data
    if (currentComponent.deliverable.id === deliverable.id) {
      const filter = del.components.filter((c) => c.id == currentComponent.id)[0]
      filter.order = event.currentIndex
      if (event.currentIndex > event.previousIndex) {
        del.components
          .filter((c) => c.id != currentComponent.id)
          .forEach((c) => {
            if (c.order <= event.currentIndex && c.order >= event.previousIndex) {
              c.order--
            }
          })
      } else {
        del.components
          .filter((c) => c.id != currentComponent.id)
          .forEach((c) => {
            if (c.order >= event.currentIndex && c.order <= event.previousIndex) {
              c.order++
            }
          })
      }
      this.deliverable = del
      this.moveComponent(this.deliverable, filter)
    } else {
      const componentForDuplicate = deliverable.components.find(
        (c) => c.source?.id === currentComponent.source?.id && c.name == currentComponent.name
      )
      if (componentForDuplicate) {
        const dialog = this.dialog.open(ScopeUiModalComponent, {
          data: new ModalConfig(
            `Duplicate component to` + deliverable.name,
            `Enter the name of the duplicated component`,
            'Complete',
            undefined,
            (form: FormControl) => {
              dialog.close()
              const duplicatedComponentName = form.get('name')?.value
              const scopeComponent = cloneDeep(currentComponent)
              scopeComponent.order = event.currentIndex
              scopeComponent.setUpdatedName(duplicatedComponentName)
              scopeComponent.name = duplicatedComponentName
              this.moveComponentOutsideDeliverable(scopeComponent, deliverable)
            },
            undefined,
            [{ name: 'name', control: new FormControl(''), type: 'text', label: 'Name' }],
            undefined,
            undefined,
            undefined,
            (form: FormControl) => {
              return form.value.name
            }
          ),
        })
      } else {
        const scopeComponent = cloneDeep(currentComponent)
        scopeComponent.order = event.currentIndex
        this.moveComponentOutsideDeliverable(scopeComponent, deliverable)
      }
    }
  }

  protected readonly trackById = trackById
}
